import { useRef, useState } from "react";
import { Upload, Plus, FileText, X, Loader2, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { parseCsv, buildLeadTemplateCsv, downloadCsv } from "@/lib/csv";
import { toast } from "sonner";

export default function LeadUploader({ onEnrich, busy, progress }) {
  const [mode, setMode] = useState("csv"); // 'csv' | 'manual'
  const [file, setFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [manual, setManual] = useState({ name: "", email: "", title: "", company: "", domain: "" });
  const fileRef = useRef(null);

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    const text = await f.text();
    const { headers, rows } = parseCsv(text);
    if (!headers.includes("company") || !headers.includes("domain")) {
      toast.error("CSV must contain 'company' and 'domain' columns.");
      setFile(null);
      setAllRows([]);
      setPreviewRows([]);
      return;
    }
    const cleaned = rows
      .map((r) => ({
        name: r.name || "",
        email: r.email || "",
        title: r.title || "",
        company: r.company || "",
        domain: r.domain || "",
        linkedin_url: r.linkedin_url || r.linkedin || "",
      }))
      .filter((r) => r.company && r.domain);
    setAllRows(cleaned);
    setPreviewRows(cleaned.slice(0, 5));
    toast.success(`Loaded ${cleaned.length} leads.`);
  };

  const submitCsv = () => {
    if (!allRows.length) {
      toast.error("No valid leads to enrich.");
      return;
    }
    onEnrich(allRows);
  };

  const submitManual = (e) => {
    e.preventDefault();
    if (!manual.company || !manual.domain) {
      toast.error("Company and domain are required.");
      return;
    }
    onEnrich([manual]);
    setManual({ name: "", email: "", title: "", company: "", domain: "", linkedin_url: "" });
  };

  const clearCsv = () => {
    setFile(null);
    setAllRows([]);
    setPreviewRows([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <div className="bg-white border border-ink-100 rounded-sm">
      {/* mode tabs */}
      <div className="flex border-b border-ink-100">
        <button
          data-testid="mode-csv-tab"
          onClick={() => setMode("csv")}
          className={`flex-1 px-6 py-4 text-xs uppercase tracking-widestx font-semibold transition-colors ${
            mode === "csv" ? "text-ink-900 bg-white" : "text-ink-400 bg-ink-50 hover:text-ink-700"
          }`}
        >
          <Upload className="w-3.5 h-3.5 inline -mt-0.5 mr-2" />
          CSV upload
        </button>
        <button
          data-testid="mode-manual-tab"
          onClick={() => setMode("manual")}
          className={`flex-1 px-6 py-4 text-xs uppercase tracking-widestx font-semibold transition-colors ${
            mode === "manual" ? "text-ink-900 bg-white" : "text-ink-400 bg-ink-50 hover:text-ink-700"
          }`}
        >
          <Plus className="w-3.5 h-3.5 inline -mt-0.5 mr-2" />
          Single lead
        </button>
      </div>

      {mode === "csv" && (
        <div className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
              Bulk upload
            </div>
            <button
              data-testid="download-template-button"
              type="button"
              onClick={() => {
                downloadCsv("leadiq-template.csv", buildLeadTemplateCsv());
                toast.success("Template downloaded — fill it in and upload.");
              }}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widestx font-semibold text-ink-700 hover:text-brand transition-colors"
            >
              <FileDown className="w-3.5 h-3.5" />
              Download template
            </button>
          </div>
          {!file ? (
            <label
              data-testid="csv-dropzone"
              htmlFor="csv-file-input"
              className="block border-2 border-dashed border-ink-200 hover:border-brand hover:bg-ink-50/50 rounded-sm px-6 py-12 text-center cursor-pointer transition-colors"
            >
              <Upload className="w-8 h-8 mx-auto text-ink-300 mb-3" strokeWidth={1.5} />
              <div className="text-sm font-medium text-ink-900">Drop CSV here or click to browse</div>
              <div className="text-xs text-ink-400 mt-2 font-mono">
                Required columns: <span className="text-ink-700">company</span>, <span className="text-ink-700">domain</span>. Optional: name, email, title, linkedin_url.
              </div>
              <input
                id="csv-file-input"
                ref={fileRef}
                data-testid="csv-file-input"
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </label>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-brand" />
                  <div>
                    <div className="text-sm font-medium text-ink-900">{file.name}</div>
                    <div className="text-xs text-ink-400 font-mono">
                      {allRows.length} valid leads detected
                    </div>
                  </div>
                </div>
                <button
                  data-testid="clear-csv-button"
                  onClick={clearCsv}
                  className="text-ink-400 hover:text-ink-700 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {previewRows.length > 0 && (
                <div className="border border-ink-100 rounded-sm overflow-x-auto mb-4">
                  <table className="w-full text-xs font-mono">
                    <thead className="bg-ink-50 text-ink-500 uppercase tracking-widestx">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Name</th>
                        <th className="text-left px-3 py-2 font-semibold">Email</th>
                        <th className="text-left px-3 py-2 font-semibold">Title</th>
                        <th className="text-left px-3 py-2 font-semibold">Company</th>
                        <th className="text-left px-3 py-2 font-semibold">Domain</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((r, idx) => (
                        <tr key={idx} className="border-t border-ink-100 text-ink-700">
                          <td className="px-3 py-2 truncate max-w-[120px]">{r.name}</td>
                          <td className="px-3 py-2 truncate max-w-[160px]">{r.email}</td>
                          <td className="px-3 py-2 truncate max-w-[120px]">{r.title}</td>
                          <td className="px-3 py-2 truncate max-w-[120px] text-ink-900 font-semibold">{r.company}</td>
                          <td className="px-3 py-2 truncate max-w-[140px]">{r.domain}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {allRows.length > previewRows.length && (
                    <div className="px-3 py-2 text-[10px] uppercase tracking-widestx text-ink-400 bg-ink-50/50 border-t border-ink-100">
                      + {allRows.length - previewRows.length} more leads
                    </div>
                  )}
                </div>
              )}

              <Button
                data-testid="enrich-csv-button"
                onClick={submitCsv}
                disabled={busy || allRows.length === 0}
                className="w-full h-11 rounded-sm bg-brand hover:bg-brand-hover text-white"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Enriching {progress.current} / {progress.total}…
                  </>
                ) : (
                  `Enrich ${allRows.length} lead${allRows.length === 1 ? "" : "s"}`
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {mode === "manual" && (
        <form onSubmit={submitManual} className="p-6 space-y-4" data-testid="manual-lead-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">Name</Label>
              <Input
                data-testid="manual-name-input"
                value={manual.name}
                onChange={(e) => setManual({ ...manual, name: e.target.value })}
                placeholder="John Smith"
                className="mt-2 rounded-sm border-ink-200 focus:border-brand h-10"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                Email <span className="text-ink-300 normal-case tracking-normal">(optional · we'll find one)</span>
              </Label>
              <Input
                data-testid="manual-email-input"
                value={manual.email}
                onChange={(e) => setManual({ ...manual, email: e.target.value })}
                placeholder="leave blank to auto-discover"
                className="mt-2 rounded-sm border-ink-200 focus:border-brand h-10 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">Title</Label>
              <Input
                data-testid="manual-title-input"
                value={manual.title}
                onChange={(e) => setManual({ ...manual, title: e.target.value })}
                placeholder="VP of IT"
                className="mt-2 rounded-sm border-ink-200 focus:border-brand h-10"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                Company <span className="text-accent">*</span>
              </Label>
              <Input
                data-testid="manual-company-input"
                required
                value={manual.company}
                onChange={(e) => setManual({ ...manual, company: e.target.value })}
                placeholder="Acme Corp"
                className="mt-2 rounded-sm border-ink-200 focus:border-brand h-10"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                Domain <span className="text-accent">*</span>
              </Label>
              <Input
                data-testid="manual-domain-input"
                required
                value={manual.domain}
                onChange={(e) => setManual({ ...manual, domain: e.target.value })}
                placeholder="acmecorp.com"
                className="mt-2 rounded-sm border-ink-200 focus:border-brand h-10 font-mono text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                LinkedIn URL <span className="text-ink-300 normal-case tracking-normal">(optional)</span>
              </Label>
              <Input
                data-testid="manual-linkedin-input"
                value={manual.linkedin_url}
                onChange={(e) => setManual({ ...manual, linkedin_url: e.target.value })}
                placeholder="https://www.linkedin.com/in/username"
                className="mt-2 rounded-sm border-ink-200 focus:border-brand h-10 font-mono text-sm"
              />
            </div>
          </div>
          <Button
            data-testid="enrich-manual-button"
            type="submit"
            disabled={busy}
            className="w-full h-11 rounded-sm bg-brand hover:bg-brand-hover text-white"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Enriching…
              </>
            ) : (
              "Enrich lead"
            )}
          </Button>
        </form>
      )}

      {/* progress bar */}
      {busy && progress.total > 0 && (
        <div className="border-t border-ink-100 px-6 py-4 bg-ink-50">
          <div className="flex items-center justify-between text-xs font-mono mb-2">
            <span className="text-ink-500 uppercase tracking-widestx">Enriching</span>
            <span className="text-ink-900 font-semibold">{progress.current} / {progress.total} · {pct}%</span>
          </div>
          <div className="h-2 w-full bg-ink-100 overflow-hidden relative">
            <div
              className="h-full bg-brand transition-all duration-500 relative"
              style={{ width: `${pct}%` }}
              data-testid="progress-bar-fill"
            >
              <div className="shimmer-overlay" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
